/**
 * Wie tief ein Modell nachdenkt — die EINE Wahrheit für Server und Oberfläche.
 * (23.09.2026)
 *
 * Martin: *„Da wir jetzt das neue Opus 5.5 haben, möchte ich bitte Vespera auf
 * die neue Version anheben … Beim Erstellen von ‚Mein Leben‘ bitte extra high
 * und ebenso beim Erstellen des täglichen Essenzspiegels."*
 *
 * ## ⚠️⚠️ Warum das jetzt ausdrücklich gesetzt werden MUSS
 *
 * Opus 5 dachte ohne Angabe auf `high`. **Opus 5.5 denkt ohne Angabe auf
 * `medium`** — eine Stufe flacher. Wer nur die Modellkennung tauscht, macht
 * Vespera also nicht stärker, sondern still oberflächlicher.
 *
 * Und genau das ist am 23.09. um 06:26 schon passiert, ohne dass jemand es
 * beschlossen hat: Das CLI-Update auf 2.1.280 hat das Kürzel `opus` auf
 * `claude-opus-5-5` umgelegt. Jeder Aufruf ohne `--effort` lief ab da auf
 * Opus 5.5 mit `medium`.
 *
 * Deshalb setzt jeder Weg die Stufe jetzt SELBST. Keiner verlässt sich mehr
 * auf eine Vorgabe, die sich mit dem nächsten Modell wieder verschiebt — und
 * auch nicht auf die verborgene `effortLevel` in pawbots settings.json, die
 * nur einige CLI-Aufrufe überhaupt lesen.
 *
 * ## Die Stufen
 *
 * Die Namen sind die der Anthropic-API (`output_config.effort`) und der CLI
 * (`--effort`). Beide kennen alle fünf auf Opus 5.5 und Fable 5.1.
 */

const DENKSTUFEN = ["low", "medium", "high", "xhigh", "max"] as const;
export type Denkstufe = (typeof DENKSTUFEN)[number];

/**
 * Was ein Weg bekommt, der nichts Besonderes braucht.
 *
 * ⚠️ `high`, nicht die Modellvorgabe: so tief, wie Opus 5 ohne Angabe dachte.
 *    Damit wird durch den Wechsel auf 5.5 nirgends etwas flacher.
 */
export const DENKSTUFE_STANDARD: Denkstufe = "high";

/**
 * Was die tiefe Denkstufe in den Einstellungen annehmen darf.
 *
 * ⚠️ Bewusst NICHT `low` und `medium`: für ein Lebensbuch und den
 *    Tagesspiegel wären sie ein Rückschritt hinter das, was vor dem
 *    Wechsel lief. Wer weniger will, wählt ein anderes Modell.
 */
export const TIEFE_DENKSTUFEN = ["high", "xhigh", "max"] as const;
export type TiefeDenkstufe = (typeof TIEFE_DENKSTUFEN)[number];

/**
 * Die Vorgabe für die zwei Werke, die Martin ausdrücklich genannt hat:
 * „Mein Leben" und der tägliche Essenz-Spiegel. „Extra high" heißt in der
 * API wörtlich `xhigh`.
 */
export const DENKSTUFE_TIEF_VORGABE: TiefeDenkstufe = "xhigh";

export function istDenkstufe(x: unknown): x is Denkstufe {
  return typeof x === "string" && (DENKSTUFEN as readonly string[]).includes(x);
}

export function istTiefeDenkstufe(x: unknown): x is TiefeDenkstufe {
  return (
    typeof x === "string" && (TIEFE_DENKSTUFEN as readonly string[]).includes(x)
  );
}

/** So heißen die Stufen dort, wo Martin sie liest. */
export const DENKSTUFE_NAME: Record<Denkstufe, string> = {
  low: "Niedrig",
  medium: "Mittel",
  high: "Hoch",
  xhigh: "Extra hoch",
  max: "Maximal",
};
